/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import {
  Box,
  Card as MuiCard,
  CardActionArea,
  CardProps as MuiCardProps,
  styled,
} from "@mui/material";
import React from "react";

import MaterialSymbol from "./MaterialSymbol";
import Typography from "./Typography";

export const StyledCard = styled(MuiCard, {
  shouldForwardProp: (prop) => prop !== "disableLink",
})<{ disableLink?: boolean }>`
  // [A11y] Applies same focus, and hover states
  :hover,
  :focus,
  :focus-within {
    ${({ theme, disableLink }) => (disableLink ? "" : theme.mixins.cardShadowHover)};
    .title {
      background-color: ${({ theme, disableLink }) =>
        disableLink ? "inherit" : theme.colors.primary50};

      border-radius: 24px;
      padding: 4px 8px;
      margin: -4px -8px;
    }

    @media (hover: none) {
      box-shadow: none;
      .title {
        background-color: transparent;
      }
    }
  }

  // [A11y] Applies focus on a link
  a:focus {
    text-decoration: underline;
  }

  // [A11y] When focus within is supported, removes focus underline from a link
  // and applies the card focus is inherited
  :focus-within a:focus {
    text-decoration: none;
  }

  position: relative;
`;

export const CardAction = styled(CardActionArea)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px;
  gap: 8px;
  .MuiCardActionArea-focusHighlight {
    background: transparent;
  }
`;

export const TitleContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
`;

interface CardTitleProps {
  title: string;
  testId?: string;
  showIcon?: boolean;
  variant?: "h2" | "h4";
}

export const CardTitle = ({ title, testId, showIcon = true, variant = "h4" }: CardTitleProps) => {
  return (
    <TitleContainer className="title">
      <Typography variant={variant} data-testid={testId}>
        {title}
      </Typography>
      {showIcon && <MaterialSymbol>arrow_right_alt</MaterialSymbol>}
    </TitleContainer>
  );
};

interface CardProps extends MuiCardProps {
  disableLink?: boolean;
}
export const Card = (props: CardProps) => {
  return (
    <StyledCard square {...(props as MuiCardProps)}>
      {props.children}
    </StyledCard>
  );
};

export default Card;
